import * as React from "react";

import { CapabilityOverridesEditor } from "@/shared/components/CapabilityOverridesEditor";
import type { OrgLlmConnectionRecord, OrgLlmModel } from "@/shared/domain/orgs";
import { useUpdateOrgLlmProvider } from "@/shared/domain/orgs/hooks";
import {
  Checkbox,
  Dialog,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";

import { TextField } from "../TextField";

import {
  modelListError,
  modelListRequest,
  modelListSaveErrorMessage,
  ownModelTestedOnSave,
} from "./modelList";
import styles from "./OrgAiModels.module.css";
import {
  emptyOwnModelDraft,
  modelsWithOwnModel,
  ownModelDraftError,
  ownModelDraftOf,
  type OwnModelDraft,
} from "./ownModelDraft";

interface OwnModelDialogProps {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The saved list; saving replaces it with the model saved in. */
  models: readonly OrgLlmModel[];
  /** The saved list's `modified`, sent with the save. */
  listModified: string;
  /** The org's connections; at least one. */
  connections: readonly OrgLlmConnectionRecord[];
  /** The own model to edit; omitted to add one. */
  model?: OrgLlmModel;
}

/**
 * Add a model on one of the org's own connections, or edit one. The org's
 * provider bills its calls, not AI credits. Saving sends the whole list; the
 * server tests a new model, or one whose model id, connection or
 * capabilities changed, and refuses the save when the test fails.
 */
export function OwnModelDialog({
  open,
  onOpenChange,
  model,
  ...formProps
}: OwnModelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={styles.dialog}>
        {open && (
          <OwnModelForm
            // A fresh form per opening and per model.
            key={model?.id ?? "new"}
            model={model}
            onDone={() => onOpenChange(false)}
            {...formProps}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function OwnModelForm({
  orgId,
  models,
  listModified,
  connections,
  model,
  onDone,
}: Omit<OwnModelDialogProps, "open" | "onOpenChange"> & {
  onDone: () => void;
}) {
  const updateMutation = useUpdateOrgLlmProvider();
  const [draft, setDraft] = React.useState<OwnModelDraft>(() =>
    model === undefined
      ? emptyOwnModelDraft(connections[0]?.connection_id ?? null)
      : ownModelDraftOf(model),
  );
  // Rule messages wait for the first Save, so an empty form doesn't open on an error.
  const [saveAttempted, setSaveAttempted] = React.useState(false);
  const update = (patch: Partial<OwnModelDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const draftError = ownModelDraftError(draft);
  const next =
    draftError === null ? modelsWithOwnModel(models, draft, model?.id) : null;
  const error = draftError ?? (next && modelListError(next.models));
  const saving = updateMutation.isPending;
  const testsOnSave = next !== null && ownModelTestedOnSave(next.entry, models);

  const handleSave = () => {
    setSaveAttempted(true);
    if (error !== null || next === null) {
      return;
    }
    updateMutation.mutate(
      { orgId, request: modelListRequest(next.models, listModified) },
      { onSuccess: onDone },
    );
  };

  return (
    <div className={styles.dialogBody}>
      <Dialog.Title>
        {model === undefined
          ? "Add model on a connection"
          : `Edit ${model.label}`}
      </Dialog.Title>
      <TextField
        label="Label"
        placeholder="Shown in the chat picker"
        value={draft.label}
        onChange={(label) => update({ label })}
        disabled={saving}
      />
      <TextField
        label="Description"
        value={draft.description}
        onChange={(description) => update({ description })}
        disabled={saving}
      />
      {/* A radio list rather than a Select: the Select popup layers below
          the Dialog. */}
      <RadioGroup<string>
        legend="Connection"
        value={draft.connection_id}
        onValueChange={(connection_id) => update({ connection_id })}
        disabled={saving}
      >
        {connections.map((c) => (
          <Radio<string>
            key={c.connection_id}
            value={c.connection_id}
            label={c.label}
          />
        ))}
      </RadioGroup>
      <TextField
        label="Provider model id"
        placeholder="e.g. claude-sonnet-5"
        value={draft.model_id}
        onChange={(model_id) => update({ model_id })}
        disabled={saving}
      />
      <TextField
        label="Context window (tokens)"
        placeholder="Deployment default"
        inputMode="numeric"
        value={draft.context_window_tokens}
        onChange={(context_window_tokens) => update({ context_window_tokens })}
        disabled={saving}
      />
      <TextField
        label="Max output tokens"
        placeholder="No limit"
        inputMode="numeric"
        value={draft.max_output_tokens}
        onChange={(max_output_tokens) => update({ max_output_tokens })}
        disabled={saving}
      />
      <CapabilityOverridesEditor
        value={draft.capability_overrides}
        onChange={(capability_overrides) => update({ capability_overrides })}
        disabled={saving}
      />
      <Checkbox
        label="In chat picker"
        description="Untick for a model that only runs titles and compression."
        checked={draft.in_picker}
        onCheckedChange={(in_picker) => update({ in_picker })}
        disabled={saving}
      />
      <p className={styles.note}>
        {model === undefined
          ? "Saving tests the model once and adds it only if the test succeeds."
          : "Changing the model id, connection or capabilities re-tests the model when you save."}
      </p>

      {saveAttempted && error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {saving && testsOnSave && (
        <p className={styles.note} role="status">
          Testing the model…
        </p>
      )}
      {updateMutation.isError && (
        <p className={styles.error} role="alert">
          {modelListSaveErrorMessage(updateMutation.error)}
        </p>
      )}
      <div className={styles.dialogActions}>
        <RobotoButton variant="secondary" onClick={onDone} disabled={saving}>
          Cancel
        </RobotoButton>
        <RobotoButton onClick={handleSave} loading={saving}>
          Save
        </RobotoButton>
      </div>
    </div>
  );
}
